import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CvpMessage } from "../generic-components/messages/CvpMessage";
import { useLocStore } from "../localization";
import { LocContext } from "../localization/LocContext";

interface WtApiContractPromotionTarget {
  environment: string;
  deployedVersion: number | null;
  state: "Ok" | "Missing" | "Unknown";
}

interface WtApiContractResponse {
  requiredVersion: number;
  deployedVersion: number | null;
  state: "Ok" | "Missing" | "Unknown";
  environment: string;
  promotionState: "Ok" | "Missing" | "Unknown" | "NotConfigured";
  promotionTargets: WtApiContractPromotionTarget[];
}

/**
 * Advisory cross-repo dependency banner (webtemp-as/agent-orchestration#22).
 * Asks the BFF whether the wtnext API it talks to carries the contract version
 * this CVp build requires (/version/WtApiContract) and warns when it does not
 * (Missing) or when the answer could not be read (Unknown - deliberately a
 * warning too, so the guard does not vanish when infrastructure is flaky).
 *
 * It also warns per promotion target (webtemp-as/webtemp-cvportal#790): an
 * environment this build can be promoted *to* whose API is behind (Missing)
 * or unreadable (Unknown) gets its own banner, so "do not promote this build
 * yet" is visible in the app rather than only on /version/WtApiContract.
 *
 * The environment gate is server-side (BFF environment name, not getEnv():
 * getEnv() maps unknown origins - e.g. dev slot ports - to "production").
 * Renders nothing in Production and nothing when every check is satisfied.
 */
export const WtApiContractWarning = () => {
  const L = useLocStore(LocContext.GENERAL);
  const [status, setStatus] = useState<WtApiContractResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/version/WtApiContract")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d) setStatus(d);
      })
      .catch(() => {
        // Advisory only - never let the check break the app.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status) return null;
  if (status.environment === "Production") return null;

  const localText =
    status.state === "Missing"
      ? L.id("e3a91b7c04d2").split(
          "Webtemp-API-et i dette miljøet har kontraktversjon {0}, men denne CVp-versjonen krever {1}. Ikke deploy denne CVp-versjonen til produksjon før API-endringen er deployet.",
          [String(status.deployedVersion ?? 0), String(status.requiredVersion)]
        )
      : status.state === "Unknown"
      ? L.id("f52c8d17a9b0").loc(
          "Fikk ikke lest kontraktversjonen fra Webtemp-API-et. Kan ikke bekrefte at API-et har endringene denne CVp-versjonen krever."
        )
      : null;

  const promotionWarnings = (status.promotionTargets ?? []).filter(
    (t) => t.state !== "Ok"
  );

  if (!localText && promotionWarnings.length === 0) return null;

  return (
    <BannerWrapper className="cvp-wt-api-contract-warning">
      {localText && (
        <CvpMessage
          severity="warn"
          icon="pi-exclamation-triangle"
          text={localText}
        />
      )}
      {promotionWarnings.map((t) => (
        <CvpMessage
          key={t.environment}
          severity="warn"
          icon="pi-exclamation-triangle"
          text={
            t.state === "Missing"
              ? L.id("b7f4c2a91e63").split(
                  "Webtemp-API-et i {0} har kontraktversjon {1}, men denne CVp-versjonen krever {2}. Ikke promoter denne CVp-versjonen til {0} før API-endringen er deployet der.",
                  [
                    t.environment,
                    String(t.deployedVersion ?? 0),
                    String(status.requiredVersion),
                  ]
                )
              : L.id("a1d8e5f27c40").split(
                  "Fikk ikke lest Webtemp-API-ets kontraktversjon i {0}. Kan ikke bekrefte at {0} er klart for denne CVp-versjonen.",
                  [t.environment]
                )
          }
        />
      ))}
    </BannerWrapper>
  );
};

const BannerWrapper = styled.div`
  display: grid;
  gap: 0.5rem;
  padding: 0.5rem 0.5rem 0 0.5rem;
`;
